import {
  canPlacePendingMatchAtScheduledSlot,
  canSwapTwoScheduledMatches,
} from '../../../../../features/schedule/scheduleOverlap';
import type {
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../../../../types/schedule';
import { isFreeSlot } from '../../../../../types/schedule';

import type { PlacementKeyboardDirection } from './schedulePlacementKeyboard';

export type SwapKeyboardTarget = {
  match: IMatchScheduleItem;
  dayKey: number;
  courtId: number;
  startMs: number;
};

export type SwapKeyboardCourt = {
  courtId: number;
  targets: SwapKeyboardTarget[];
};

export type SwapKeyboardDay = {
  dayKey: number;
  courts: SwapKeyboardCourt[];
};

export type SwapKeyboardContext = {
  days: SwapKeyboardDay[];
};

export function isValidSwapKeyboardTarget(
  activeMatch: IMatchScheduleItem,
  target: IMatchScheduleItem,
  allScheduledMatchesFlat: IMatchScheduleItem[],
  scheduleConstraints: IScheduleConstraints,
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null
): boolean {
  if (!target.scheduleDate || !target.court) return false;
  if (target.id === activeMatch.id) return false;

  const sourceIsScheduledPairSwap = pendingPlacementSource === 'scheduled_grid';

  return sourceIsScheduledPairSwap
    ? canSwapTwoScheduledMatches(
        activeMatch,
        target,
        allScheduledMatchesFlat,
        scheduleConstraints
      )
    : canPlacePendingMatchAtScheduledSlot(
        activeMatch,
        target,
        allScheduledMatchesFlat,
        scheduleConstraints
      );
}

export function buildSwapKeyboardContext(
  displaySchedule: ITournamentSchedule,
  activeMatch: IMatchScheduleItem,
  allScheduledMatchesFlat: IMatchScheduleItem[],
  scheduleConstraints: IScheduleConstraints,
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null
): SwapKeyboardContext {
  const days: SwapKeyboardDay[] = displaySchedule.days.map((day) => ({
    dayKey: day.dayKey,
    courts: day.courts.map((courtSchedule) => {
      const targets = courtSchedule.matches
        .filter(
          (item): item is IMatchScheduleItem => !isFreeSlot(item) && !!item.scheduleDate
        )
        .map((match) => ({
          match,
          dayKey: day.dayKey,
          courtId: courtSchedule.court.id,
          startMs: new Date(match.scheduleDate!).getTime(),
        }))
        .filter((entry) =>
          isValidSwapKeyboardTarget(
            activeMatch,
            entry.match,
            allScheduledMatchesFlat,
            scheduleConstraints,
            pendingPlacementSource
          )
        )
        .sort((a, b) => a.startMs - b.startMs);
      return { courtId: courtSchedule.court.id, targets };
    }),
  }));

  return { days };
}

export type SwapSeedAnchor = {
  courtId?: number;
  startMs?: number;
};

/** Court distance dominates time when scoring nearest swap target. */
const COURT_DISTANCE_PRIORITY = 1_000_000_000_000;

/** Pick the valid swap target nearest the placement anchor (selected match or focused slot). */
export function seedFocusedSwapTarget(
  context: SwapKeyboardContext,
  anchor?: SwapSeedAnchor
): IMatchScheduleItem | null {
  const targets: SwapKeyboardTarget[] = [];
  for (const day of context.days) {
    for (const court of day.courts) {
      targets.push(...court.targets);
    }
  }
  if (targets.length === 0) return null;

  const anchorCourtId = anchor?.courtId;
  const anchorStartMs = anchor?.startMs;
  if (anchorCourtId == null && anchorStartMs == null) {
    return targets[0].match;
  }

  const courtId = anchorCourtId ?? targets[0].courtId;
  const startMs = anchorStartMs ?? targets[0].startMs;

  let best = targets[0];
  let bestScore = Number.POSITIVE_INFINITY;
  for (const entry of targets) {
    const courtDist = Math.abs(entry.courtId - courtId);
    const timeDist = Math.abs(entry.startMs - startMs);
    const score = courtDist * COURT_DISTANCE_PRIORITY + timeDist;
    if (score < bestScore) {
      bestScore = score;
      best = entry;
    }
  }
  return best.match;
}

function findTargetPosition(
  context: SwapKeyboardContext,
  matchId: number
): { day: SwapKeyboardDay; court: SwapKeyboardCourt; index: number } | null {
  for (const day of context.days) {
    for (const court of day.courts) {
      const index = court.targets.findIndex((t) => t.match.id === matchId);
      if (index >= 0) return { day, court, index };
    }
  }
  return null;
}

export function getNextFocusedSwapTarget(
  direction: PlacementKeyboardDirection,
  current: IMatchScheduleItem,
  context: SwapKeyboardContext
): IMatchScheduleItem {
  const pos = findTargetPosition(context, current.id);
  if (!pos) return current;

  const { day, court, index } = pos;
  const currentStartMs = new Date(current.scheduleDate!).getTime();

  if (direction === 'up' || direction === 'down') {
    const delta = direction === 'down' ? 1 : -1;
    const nextTarget = court.targets[index + delta];
    return nextTarget?.match ?? current;
  }

  const courtIndex = day.courts.findIndex((c) => c.courtId === court.courtId);
  if (courtIndex < 0) return current;

  const step = direction === 'right' ? 1 : -1;
  for (let i = courtIndex + step; i >= 0 && i < day.courts.length; i += step) {
    const targetCourt = day.courts[i];
    if (targetCourt.targets.length === 0) continue;

    let best = targetCourt.targets[0];
    let bestDist = Math.abs(best.startMs - currentStartMs);
    for (const candidate of targetCourt.targets) {
      const dist = Math.abs(candidate.startMs - currentStartMs);
      if (dist < bestDist) {
        best = candidate;
        bestDist = dist;
      }
    }
    return best.match;
  }

  return current;
}
